// החלפת גרסה בשרת (12 §1 — deploy בלי השבתה): בזמן ההחלפה nginx עונה 503 + Retry-After (או 502/רשת לרגע).
// GET נשלח שוב עם backoff (api.ts); POST לא נשלח שוב — אין לדעת אם כבר בוצע. כל תשובה/כשל מדווחים ל-health.ts,
// ש-ConnectionPill מציג ממנו גלולה רגועה ("מעדכנים את המערכת — חוזרים בעוד רגע") ו"חזרנו" בסוף.
import { reportOk, reportTrouble } from './health.ts';

/** סטטוסים של "השרת מתחלף" — לא תקלה באפליקציה. */
export const TRANSIENT_STATUSES = new Set([502, 503, 504]);

/** השהיה לפני ניסיון n (0-based): 0.5s, 1s, 2s, 4s, 8s…; Retry-After (שניות) גובר כשהוא ארוך יותר, עד 10s. */
export function retryDelayMs(n: number, retryAfter: string | null): number {
  const base = Math.min(8000, 500 * 2 ** n);
  const ra = retryAfter && /^\d+$/.test(retryAfter.trim()) ? Number(retryAfter) * 1000 : 0;
  return Math.min(10_000, Math.max(base, ra));
}

/** כמה ניסיונות חוזרים ל-GET (סה"כ ~30s — deploy רגיל מתחלף בשניות). */
export const GET_RETRIES = 6;

const sleepMs = (ms: number, signal?: AbortSignal | null) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done);
  });

/**
 * fetch עם ניסיון חוזר בזמן החלפת גרסה (deploy): רק GET/HEAD (אידמפוטנטי), על שגיאת רשת או 502/503/504, עם backoff
 * ו-Retry-After; המצב מדווח ל-health.ts (גלולה אחת לכל האפליקציה). POST/PUT/DELETE — ניסיון אחד (אולי כבר בוצע). SSE (accept:
 * text/event-stream) — בלי, כי ל-openSse לולאת reconnect משלה. כשל רשת סופי → fail().
 */
export async function fetchRetrying(path: string, init: RequestInit, fail: () => Error): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const accept = new Headers(init.headers).get('accept') ?? '';
  const retries = (method === 'GET' || method === 'HEAD') && !accept.includes('text/event-stream') ? GET_RETRIES : 0;
  for (let n = 0; ; n++) {
    let r: Response | null = null;
    try {
      r = await fetch(path, init);
    } catch {
      if (init.signal?.aborted) throw fail();
      reportTrouble(typeof navigator !== 'undefined' && !(globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine ? 'offline' : 'error');
      if (n >= retries) throw fail();
    }
    if (r && !TRANSIENT_STATUSES.has(r.status)) {
      reportOk();
      return r;
    }
    if (r) reportTrouble('updating');
    if (r && n >= retries) return r;
    await sleepMs(retryDelayMs(n, r?.headers.get('retry-after') ?? null), init.signal);
    if (init.signal?.aborted) throw fail();
  }
}
